/**
 * CC Quote Request System
 * Multi-product quote cart with sidebar management
 */
(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    storageKey: 'cc_quote_items',
    formId: 'quote-request-form',
    apiUrl: (window.CC_QUOTE_CONFIG && window.CC_QUOTE_CONFIG.apiUrl) || 'https://cobolt-quote-api.onrender.com',
    apiKey: (window.CC_QUOTE_CONFIG && window.CC_QUOTE_CONFIG.apiKey) || ''
  };

  /**
   * Quote Cart - Manages multi-product quote list
   */
  const QuoteCart = {
    items: [],

    /**
     * Initialize cart from localStorage
     */
    init: function() {
      this.load();
      this.updateUI();
      this.attachEventListeners();
    },

    /**
     * Load items from localStorage
     */
    load: function() {
      try {
        const data = localStorage.getItem(CONFIG.storageKey);
        this.items = data ? JSON.parse(data) : [];
      } catch (error) {
        console.error('Error loading quote cart:', error);
        this.items = [];
      }
    },

    /**
     * Save items to localStorage
     */
    save: function() {
      try {
        localStorage.setItem(CONFIG.storageKey, JSON.stringify(this.items));
      } catch (error) {
        console.error('Error saving quote cart:', error);
      }
    },

    /**
     * Generate unique ID for quote item
     */
    generateId: function(variantId, options, boldOptions) {
      const allOptions = [...(options || []), ...(boldOptions || [])];
      const optionsStr = allOptions.length > 0 ? JSON.stringify(allOptions) : '';
      return `${variantId}-${btoa(optionsStr)}`;
    },

    /**
     * Add item to quote cart
     * @param {Object} item - Product data
     */
    add: function(item) {
      const id = this.generateId(item.variantId, item.options, item.boldOptions);

      // Check if item already exists (same variant and options)
      const existingIndex = this.items.findIndex(i => i.id === id);

      if (existingIndex > -1) {
        // Update quantity
        this.items[existingIndex].quantity += item.quantity;
      } else {
        // Add new item
        this.items.push({
          id: id,
          productId: item.productId,
          variantId: item.variantId,
          title: item.title,
          variantTitle: item.variantTitle || '',
          image: item.image || '',
          sku: item.sku || '',
          price: item.price || '0.00',
          url: item.url || '',
          quantity: item.quantity || 1,
          options: item.options || [],
          boldOptions: item.boldOptions || [],
          addedAt: new Date().toISOString()
        });
      }

      this.save();
      this.updateUI();

      // Open sidebar
      window.dispatchEvent(new CustomEvent('open-quote-sidebar'));
    },

    /**
     * Remove item from cart
     * @param {string} id - Item ID
     */
    remove: function(id) {
      this.items = this.items.filter(item => item.id !== id);
      this.save();
      this.updateUI();
    },

    /**
     * Update item quantity
     * @param {string} id - Item ID
     * @param {number} quantity - New quantity
     */
    updateQuantity: function(id, quantity) {
      const item = this.items.find(i => i.id === id);
      if (item) {
        item.quantity = Math.max(1, quantity);
        this.save();
        this.updateUI();
      }
    },

    /**
     * Clear all items
     */
    clear: function() {
      this.items = [];
      this.save();
      this.updateUI();
    },

    /**
     * Get total item count
     */
    getCount: function() {
      return this.items.reduce((total, item) => total + item.quantity, 0);
    },

    /**
     * Update all UI elements
     */
    updateUI: function() {
      this.updateHeaderCount();
      this.renderItems();
      this.toggleFormVisibility();
    },

    /**
     * Update header count badges
     */
    updateHeaderCount: function() {
      const count = this.getCount();
      const countElements = document.querySelectorAll('[data-quote-item-count]');
      const countTextElements = document.querySelectorAll('[data-quote-item-count-text]');

      countElements.forEach(el => {
        el.textContent = count;
        el.style.display = 'flex';
      });

      countTextElements.forEach(el => {
        el.textContent = count;
      });
    },

    /**
     * Render quote items in sidebar
     */
    renderItems: function() {
      const container = document.querySelector('[data-quote-items]');
      const emptyState = document.querySelector('[data-quote-empty]');

      if (!container) return;

      if (this.items.length === 0) {
        container.innerHTML = '';
        if (emptyState) emptyState.style.display = 'block';
        return;
      }

      if (emptyState) emptyState.style.display = 'none';

      let html = '';

      this.items.forEach(item => {
        const optionsHtml = this.formatOptions(item.options, item.boldOptions);
        const hasDetails = (item.variantTitle && item.variantTitle !== 'Default Title') || item.sku || optionsHtml;

        html += `
          <div class="quote-item-card" data-quote-item-id="${item.id}">
            <img
              src="${item.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}"
              alt="${this.escapeHtml(item.title)}"
              class="quote-item-image"
            >
            <div class="quote-item-details">
              <a href="${item.url}" class="quote-item-title" title="${this.escapeHtml(item.title)}">${this.escapeHtml(item.title)}</a>
              ${hasDetails ? `
                <button type="button" class="quote-view-details" data-action="toggle-details" data-id="${item.id}">
                  <span class="quote-view-text">View Details</span> <span class="toggle-icon">▼</span>
                </button>
                <div class="quote-item-options-panel" style="display: none;" data-options-panel="${item.id}">
                  <ul class="quote-item-options-list">
                    ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<li>${this.escapeHtml(item.variantTitle)}</li>` : ''}
                    ${item.sku ? `<li>SKU: ${this.escapeHtml(item.sku)}</li>` : ''}
                    ${this.formatOptionsAsList(item.options, item.boldOptions)}
                  </ul>
                </div>
              ` : ''}
              <div class="quote-item-qty-row">
                <div class="quote-item-qty-controls">
                  <button type="button" class="quote-qty-btn" data-action="decrease" data-id="${item.id}">−</button>
                  <input
                    type="number"
                    class="quote-qty-input"
                    value="${item.quantity}"
                    min="1"
                    data-id="${item.id}"
                  >
                  <button type="button" class="quote-qty-btn" data-action="increase" data-id="${item.id}">+</button>
                </div>
                <button type="button" class="quote-remove-btn" data-action="remove" data-id="${item.id}" aria-label="Remove item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        `;
      });

      container.innerHTML = html;
    },

    /**
     * Format options for display
     */
    formatOptions: function(options, boldOptions) {
      const allOptions = [...(options || []), ...(boldOptions || [])];
      if (allOptions.length === 0) return '';

      return allOptions
        .filter(opt => opt.name && opt.value)
        .map(opt => `${this.escapeHtml(opt.name)}: ${this.escapeHtml(opt.value)}`)
        .join(' | ');
    },

    /**
     * Format options as list items for bullet display
     */
    formatOptionsAsList: function(options, boldOptions) {
      const allOptions = [...(options || []), ...(boldOptions || [])];
      if (allOptions.length === 0) return '';

      return allOptions
        .filter(opt => opt.name && opt.value)
        .map(opt => `<li>${this.escapeHtml(opt.name)}: ${this.escapeHtml(opt.value)}</li>`)
        .join('');
    },

    /**
     * Escape HTML special characters
     */
    escapeHtml: function(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },

    /**
     * Toggle form visibility based on cart contents
     */
    toggleFormVisibility: function() {
      const hasItems = this.items.length > 0;
      const formContainer = document.querySelector('[data-quote-form-container]');
      if (formContainer) {
        formContainer.style.display = hasItems ? 'block' : 'none';
      }
      const addMorePrompt = document.querySelector('[data-quote-add-more]');
      if (addMorePrompt) {
        addMorePrompt.style.display = hasItems ? 'block' : 'none';
      }
    },

    /**
     * Attach event listeners for cart interactions
     */
    attachEventListeners: function() {
      // Delegate events for quote items
      document.addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]');
        if (!action) return;

        const actionType = action.dataset.action;
        const id = action.dataset.id;

        if (actionType === 'remove') {
          this.remove(id);
        } else if (actionType === 'increase') {
          const item = this.items.find(i => i.id === id);
          if (item) {
            this.updateQuantity(id, item.quantity + 1);
          }
        } else if (actionType === 'decrease') {
          const item = this.items.find(i => i.id === id);
          if (item && item.quantity > 1) {
            this.updateQuantity(id, item.quantity - 1);
          }
        } else if (actionType === 'toggle-details') {
          const panel = document.querySelector(`[data-options-panel="${id}"]`);
          const button = action;
          if (panel) {
            const isOpen = panel.style.display !== 'none';
            panel.style.display = isOpen ? 'none' : 'block';
            button.classList.toggle('is-open', !isOpen);
          }
        }
      });

      // Quantity input change
      document.addEventListener('change', (e) => {
        if (e.target.matches('.quote-qty-input')) {
          const id = e.target.dataset.id;
          const quantity = parseInt(e.target.value, 10) || 1;
          this.updateQuantity(id, quantity);
        }
      });
    }
  };

  /**
   * Bold Options Capture - Handles Bold Product Options
   */
  const BoldOptionsCapture = {
    /**
     * Check if a property name is a system/internal property
     */
    isSystemProperty: function(propertyName) {
      return /^s\[/.test(propertyName) ||
             /\ss\[/.test(propertyName) ||
             /^Option\ss\[/.test(propertyName) ||
             /^_/.test(propertyName) ||
             /^bold_/i.test(propertyName);
    },

    /**
     * Capture Bold Options from the page
     */
    capture: function() {
      const options = [];

      // Capture radio button options
      const radioInputs = document.querySelectorAll('.bold_option_radio input[type="radio"]:checked');
      radioInputs.forEach(input => {
        if (input.name && input.name.startsWith('properties[')) {
          const match = input.name.match(/properties\[(.*?)\]/);
          if (match) {
            const optionName = match[1].replace(/\*/g, '').trim();
            if (!this.isSystemProperty(optionName)) {
              options.push({ name: optionName, value: input.value });
            }
          }
        }
      });

      // Capture swatch options
      const swatchHelpers = document.querySelectorAll('.bold_option_swatch input[type="hidden"][class*="_helper"]');
      swatchHelpers.forEach(input => {
        if (input.name && input.name.startsWith('properties[') && input.value) {
          const match = input.name.match(/properties\[(.*?)\]/);
          if (match) {
            const optionName = match[1].replace(/\*/g, '').trim();
            if (!this.isSystemProperty(optionName)) {
              options.push({ name: optionName, value: input.value });
            }
          }
        }
      });

      // Capture text/number inputs
      const textInputs = document.querySelectorAll('.bold_option input[type="text"], .bold_option input[type="number"]');
      textInputs.forEach(input => {
        if (input.name && input.name.startsWith('properties[') && input.value) {
          const match = input.name.match(/properties\[(.*?)\]/);
          if (match) {
            const optionName = match[1].replace(/\*/g, '').trim();
            if (!this.isSystemProperty(optionName)) {
              options.push({ name: optionName, value: input.value });
            }
          }
        }
      });

      // Capture select options
      const selectInputs = document.querySelectorAll('.bold_option select');
      selectInputs.forEach(select => {
        if (select.name && select.name.startsWith('properties[') && select.value) {
          const match = select.name.match(/properties\[(.*?)\]/);
          if (match) {
            const optionName = match[1].replace(/\*/g, '').trim();
            if (!this.isSystemProperty(optionName)) {
              options.push({ name: optionName, value: select.value });
            }
          }
        }
      });

      return options;
    }
  };

  /**
   * Add to Quote Button Handler
   */
  const AddToQuoteHandler = {
    /**
     * Initialize button handlers
     */
    init: function() {
      document.addEventListener('click', (e) => {
        const button = e.target.closest('.cc-add-to-quote');
        if (button) {
          e.preventDefault();
          this.handleClick(button);
        }
      });
    },

    /**
     * Handle add to quote button click
     */
    handleClick: function(button) {
      // Get quantity from shared quantity input
      const quantityInput = document.querySelector('#quantity, [name="quantity"], .js-input-quantity');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // Get product data from button attributes
      const productData = {
        productId: button.dataset.productId || '',
        variantId: button.dataset.variantId || '',
        title: button.dataset.productTitle || '',
        variantTitle: button.dataset.variantTitle || '',
        image: button.dataset.productImage || '',
        sku: button.dataset.productSku || '',
        price: button.dataset.productPrice
          ? (parseInt(button.dataset.productPrice, 10) / 100).toFixed(2)
          : '0.00',
        url: button.dataset.productUrl || '',
        quantity: quantity,
        options: this.captureVariantOptions(),
        boldOptions: BoldOptionsCapture.capture()
      };

      // Add visual feedback
      this.showAddingState(button);

      // Add to cart
      QuoteCart.add(productData);

      // Reset button state
      setTimeout(() => {
        this.resetButton(button);
      }, 500);
    },

    /**
     * Capture variant options (Size, Color, etc.)
     */
    captureVariantOptions: function() {
      const options = [];

      // Capture from select dropdowns
      const selectOptions = document.querySelectorAll('select[name^="option"]');
      selectOptions.forEach((select, index) => {
        if (select.value) {
          const label = document.querySelector(`label[for="${select.id}"]`);
          const name = label ? label.textContent.trim() : `Option ${index + 1}`;
          options.push({ name: name, value: select.value });
        }
      });

      // Capture from radio buttons (swatches)
      const radioOptions = document.querySelectorAll('.product-form__input input[type="radio"]:checked, .variant-input:checked');
      radioOptions.forEach(radio => {
        const fieldset = radio.closest('fieldset');
        const legend = fieldset ? fieldset.querySelector('legend') : null;
        const name = legend ? legend.textContent.trim().replace(':', '') : 'Option';
        const label = document.querySelector(`label[for="${radio.id}"]`);
        const value = label ? label.textContent.trim() : radio.value;
        options.push({ name: name, value: value });
      });

      return options;
    },

    /**
     * Show adding state on button
     */
    showAddingState: function(button) {
      button.classList.add('cc-quote-adding');
      const textSpan = button.querySelector('.cc-quote-btn-text');
      if (textSpan) {
        textSpan.dataset.originalText = textSpan.textContent;
        textSpan.textContent = 'Adding';
      }
    },

    /**
     * Reset button to normal state
     */
    resetButton: function(button) {
      button.classList.remove('cc-quote-adding');
      const textSpan = button.querySelector('.cc-quote-btn-text');
      if (textSpan && textSpan.dataset.originalText) {
        textSpan.textContent = textSpan.dataset.originalText;
      }
    }
  };

  /**
   * Quote Form Handler
   */
  const QuoteFormHandler = {
    /**
     * Initialize form handler
     */
    init: function() {
      const form = document.getElementById(CONFIG.formId);
      if (form) {
        form.addEventListener('submit', (e) => this.handleSubmit(e));
      }
    },

    /**
     * Handle form submission
     */
    handleSubmit: async function(e) {
      e.preventDefault();

      const form = e.target;
      const formData = new FormData(form);
      const submitBtn = form.querySelector('#quote-submit-btn');
      const messageEl = document.querySelector('[data-quote-message]');

      // Validate
      const name = formData.get('name');
      const email = formData.get('email');
      const phone = formData.get('phone');

      if (!name || !email || !phone) {
        this.showMessage(messageEl, 'Please fill in all required fields', 'error');
        return;
      }

      // Disable submit button
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';
      }

      try {
        // Submit quote
        const result = await this.submitQuote({
          name: name,
          email: email,
          phone: phone,
          message: formData.get('message') || '',
          items: QuoteCart.items
        });

        // Success — clear cart, close sidebar, show lightbox
        var quoteId = result.data && result.data.quoteId ? result.data.quoteId : '';
        QuoteCart.clear();
        form.reset();

        // Close sidebar immediately
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));

        // Show success lightbox
        this.showSuccessLightbox(quoteId);

      } catch (error) {
        console.error('Quote submission error:', error);
        this.showMessage(messageEl, 'Failed to send quote request. Please try again.', 'error');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit Quote Request';
        }
      }
    },

    /**
     * Submit quote to Cobolt Quote API
     */
    submitQuote: async function(data) {
      const payload = {
        customer: {
          name: data.name,
          email: data.email,
          phone: data.phone || undefined,
          message: data.message || undefined
        },
        items: data.items.map(function(item) {
          return {
            variantId: item.variantId || undefined,
            title: item.title,
            quantity: item.quantity,
            price: item.price || undefined,
            sku: item.sku || undefined,
            variantTitle: item.variantTitle || undefined,
            options: item.options && item.options.length > 0 ? item.options : undefined,
            boldOptions: item.boldOptions && item.boldOptions.length > 0 ? item.boldOptions : undefined
          };
        })
      };

      const headers = { 'Content-Type': 'application/json' };
      if (CONFIG.apiKey) {
        headers['X-API-Key'] = CONFIG.apiKey;
      }

      const response = await fetch(CONFIG.apiUrl + '/api/quote', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorMsg = result.error && result.error.message
          ? result.error.message
          : 'Failed to submit quote request';
        throw new Error(errorMsg);
      }

      return result;
    },

    /**
     * Show message in form
     */
    showMessage: function(el, text, type) {
      if (!el) return;

      el.textContent = text;
      el.className = 'quote-message ' + type;
      el.classList.remove('hidden');

      if (type === 'success') {
        setTimeout(() => {
          el.classList.add('hidden');
        }, 5000);
      }
    },

    /**
     * Show success lightbox with quote ID (uses safe DOM methods, no innerHTML)
     */
    showSuccessLightbox: function(quoteId) {
      var existing = document.getElementById('cc-quote-lightbox');
      if (existing) existing.remove();

      // Build overlay
      var overlay = document.createElement('div');
      overlay.id = 'cc-quote-lightbox';
      overlay.className = 'cc-quote-lightbox-overlay';

      // Build card
      var card = document.createElement('div');
      card.className = 'cc-quote-lightbox-card';

      // Checkmark icon
      var iconWrap = document.createElement('div');
      iconWrap.className = 'cc-quote-lightbox-icon';
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '48');
      svg.setAttribute('height', '48');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      var circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '10');
      circle.setAttribute('stroke', '#0F6B6B');
      circle.setAttribute('stroke-width', '1.5');
      var checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      checkPath.setAttribute('d', 'M8 12.5L10.5 15L16 9');
      checkPath.setAttribute('stroke', '#0F6B6B');
      checkPath.setAttribute('stroke-width', '1.5');
      checkPath.setAttribute('stroke-linecap', 'round');
      checkPath.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(circle);
      svg.appendChild(checkPath);
      iconWrap.appendChild(svg);
      card.appendChild(iconWrap);

      // Heading
      var heading = document.createElement('h3');
      heading.className = 'cc-quote-lightbox-heading';
      heading.textContent = 'Quote Submitted';
      card.appendChild(heading);

      // Quote ID — click to copy
      if (quoteId) {
        var idEl = document.createElement('span');
        idEl.className = 'cc-quote-lightbox-id';
        idEl.textContent = quoteId;
        idEl.setAttribute('title', 'Click to copy');

        function fallbackCopy(text) {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          var ok = false;
          try { ok = document.execCommand('copy'); } catch (e) { /* ignore */ }
          ta.remove();
          return ok;
        }

        idEl.addEventListener('click', function() {
          function onCopied() {
            idEl.textContent = 'Copied!';
            setTimeout(function() { idEl.textContent = quoteId; }, 2000);
          }

          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(quoteId).then(onCopied).catch(function() {
              fallbackCopy(quoteId) && onCopied();
            });
          } else {
            fallbackCopy(quoteId) && onCopied();
          }
        });
        card.appendChild(idEl);

        var hint = document.createElement('p');
        hint.className = 'cc-quote-lightbox-copy-hint';
        hint.textContent = 'Click to copy';
        card.appendChild(hint);
      }

      // Message
      var msg = document.createElement('p');
      msg.className = 'cc-quote-lightbox-message';
      msg.textContent = 'Thank you for your quote request. We\'ll review your items and get back to you shortly.';
      card.appendChild(msg);

      // Close button
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'cc-quote-lightbox-close-btn';
      closeBtn.textContent = 'Close';
      card.appendChild(closeBtn);

      overlay.appendChild(card);
      document.body.appendChild(overlay);

      // Prevent body scroll
      document.body.classList.add('overflow-hidden');

      // Close handlers
      function closeLightbox() {
        overlay.remove();
        document.body.classList.remove('overflow-hidden');
      }

      closeBtn.addEventListener('click', closeLightbox);
      overlay.addEventListener('click', function(e) {
        if (e.target === overlay) closeLightbox();
      });
    }
  };

  /**
   * Initialize the quote request system
   */
  function init() {
    QuoteCart.init();
    AddToQuoteHandler.init();
    QuoteFormHandler.init();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.CCQuoteCart = QuoteCart;

})();
